import { getDB } from "./storageService";
import { deliverFileToS3 } from "./batchDelivery";
import { secureFetch } from "./batchSecurity";

/**
 * 🎯 THE AUTHORITATIVE QUEUE MANAGER
 * Orchestrates concurrency, telemetry pulsing, and granular file/batch controls.
 */
class QueueManager {
  constructor(concurrency = 3) {
    this.concurrency = concurrency;
    this.activeCount = 0;
    this.queue = [];
    this.onProgressUpdate = null;
    this.pausedBatches = new Set();
    this.pausedFiles = new Set();    // 🎯 Tracks interrupted files: "batchId-fileName"
    this.activeRequests = new Map(); // 🎯 Tracks active XHR objects for .abort()
  }

  /**
   * ⏸️ FILE-LEVEL: INTERRUPT / CONTINUE
   * Toggles the pause state and kills active network streams.
   */
  toggleFilePause(batchId, fileName) {
    const key = `${batchId}-${fileName}`;
    if (this.pausedFiles.has(key)) {
      this.pausedFiles.delete(key);
      console.log(`▶️ CONTINUE: ${fileName}`);
      this.processNext();
    } else {
      this.pausedFiles.add(key);
      console.log(`⏸️ INTERRUPT: ${fileName}`);
      // Kill the bits moving over the wire immediately
      if (this.activeRequests.has(key)) {
        this.activeRequests.get(key).abort();
        this.activeRequests.delete(key);
      }
    }
  }

  /**
   * 🛑 FILE-LEVEL: STOP & RESET (0%)
   * Aborts the transfer and overwrites local progress records.
   */
  async stopAndResetFile(batchId, fileName, siteKey) {
    const key = `${batchId}-${fileName}`;
    
    // 1. Kill active network connection
    if (this.activeRequests.has(key)) {
      this.activeRequests.get(key).abort();
      this.activeRequests.delete(key);
    }
    
    // 2. Mark as paused/stopped so it doesn't auto-restart
    this.pausedFiles.add(key);

    // 3. Reset local database state to zero
    const db = getDB(siteKey);
    await db.files.where({ batchId, name: fileName }).modify({ 
      progress: 0, 
      bytesUploaded: 0,
      status: "Staged" 
    });

    // 4. Pulse the UI to show 0% immediately
    if (this.onProgressUpdate) this.onProgressUpdate(batchId, fileName, 0);
    
    console.log(`🛑 STOPPED & RESET: ${fileName}`);
  }

  /**
   * 🚫 FILE-LEVEL: CANCEL (PURGE)
   * Removes the file from the queue and database permanently.
   */
  async cancelFile(batchId, fileName, siteKey) {
    const key = `${batchId}-${fileName}`;
    
    if (this.activeRequests.has(key)) {
      this.activeRequests.get(key).abort();
      this.activeRequests.delete(key);
    }

    this.queue = this.queue.filter(t => !(t.batchId === batchId && t.file.name === fileName));
    this.pausedFiles.delete(key);

    const db = getDB(siteKey);
    await db.files.where({ batchId, name: fileName }).delete();
    
    console.log(`🗑️ PURGED: ${fileName} removed from local storage.`);
    this.processNext();
  }

  togglePause(batchId) {
    if (this.pausedBatches.has(batchId)) {
      this.pausedBatches.delete(batchId);
      console.log(`▶️ RESUMING: Batch ${batchId}`);
      this.processNext();
    } else {
      this.pausedBatches.add(batchId);
      console.log(`⏸ PAUSED: Batch ${batchId}`);
    }
  }

  cancelBatch(batchId) {
    const originalLength = this.queue.length;
    this.queue = this.queue.filter(task => task.batchId !== batchId);
    console.log(`🚫 CANCELLED: Batch ${batchId}. Purged ${originalLength - this.queue.length} tasks.`);
  }

  async enqueue(batchId, files, siteKey, onProgress) {
    this.onProgressUpdate = onProgress;
    console.log(`📥 ENQUEUE: Adding ${files.length} files for Batch ${batchId}`);

    const tasks = files.map(file => ({
      batchId,
      file,
      siteKey
    }));

    this.queue.push(...tasks);
    this.processNext();
  }

  async processNext() {
    if (this.activeCount >= this.concurrency || this.queue.length === 0) {
      return;
    }

    // Filter logic: Must not be in a paused batch AND must not be an interrupted file
    const taskIndex = this.queue.findIndex(t => 
      !this.pausedBatches.has(t.batchId) && 
      !this.pausedFiles.has(`${t.batchId}-${t.file.name}`)
    );
    
    if (taskIndex === -1) return;

    this.activeCount++;
    const { batchId, file, siteKey } = this.queue.splice(taskIndex, 1)[0];
    const db = getDB(siteKey);
    const requestKey = `${batchId}-${file.name}`;

    console.log(`🚀 DISPATCH: ${file.name} (Batch ${batchId}) | Active: ${this.activeCount}/${this.concurrency}`);

    try {
      const fileRecord = await db.files.where({ batchId, name: file.name }).first();

      if (fileRecord) {
        /**
         * ⚡ THE PULSE GENERATOR
         * Updated to capture the XHR object for later interruption.
         */
        await deliverFileToS3(fileRecord, siteKey, async (percent, xhr) => {
          // Store the handle so we can .abort() if the user hits Pause/Stop
          if (xhr && !this.activeRequests.has(requestKey)) {
            this.activeRequests.set(requestKey, xhr);
          }

          // 1. Sync to Dexie (for persistence)
          await db.files.where({ batchId, name: file.name }).modify({ 
            progress: percent,
            bytesUploaded: Math.round((percent / 100) * fileRecord.size)
          });

          // 2. Pulse to Context (for animation)
          if (typeof this.onProgressUpdate === 'function') {
            this.onProgressUpdate(batchId, file.name, percent);
          }
        });

        this.activeRequests.delete(requestKey);

        // 🔄 BRIDGE: Authority Update to SQL
        await secureFetch(`/api/v1/batch/update-file-progress`, "POST", siteKey, "UpdateProgress", {
          batchId: batchId,
          batchStatus: 20,
          fileName: file.name,
          fileStatus: 40,
          bytesTransferred: fileRecord.size,
          isComplete: true
        });

        await db.files.where({ batchId, name: file.name }).modify({ status: "Delivered" });
      }
    } catch (err) {
      this.activeRequests.delete(requestKey);
      console.error(`❌ ERROR: ${file.name}`, err);
      // Logic for error reporting to SQL remains same
      await db.files.where({ batchId, name: file.name }).modify({ status: "Failed" });
    } finally {
      this.activeCount--;
      await this.checkBatchCompletion(batchId, db, siteKey);
      this.processNext();
    }
  }

  async checkBatchCompletion(batchId, db, siteKey) {
    const remainingCount = await db.files
      .where({ batchId })
      .filter(f => f.status !== "Delivered" && f.status !== "Failed")
      .count();

    if (remainingCount === 0) {
      const files = await db.files.where({ batchId }).toArray();
      const hasErrors = files.some(f => f.status === "Failed");
      const finalStatus = hasErrors ? 30 : 40;

      await secureFetch(`/api/v1/batch/update-status`, "POST", siteKey, "Finalize", {
        batchId: batchId,
        newStatus: finalStatus,
        siteUserGuid: sessionStorage.getItem("SEC_USER_GUID"),
        details: hasErrors ? "Completed with errors" : "Batch successfully delivered"
      });

      await db.batches.where({ batchId }).modify({ status: "Delivered" });
      console.log(`🏆 COMPLETE: Batch ${batchId} finalized.`);
    }
  }
}

export const queueManager = new QueueManager(3);